import React from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ServiceLayout } from '@/components/ServiceLayout';
import { Loader2, Settings as SettingsIcon, Hash, Info, ShieldAlert } from 'lucide-react';
import FieldVisibilitySection from '@/components/Service/FieldVisibilitySection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Service-module tenant configuration page.
 * Currently hosts a single toggle (auto-generate Job-No) but is structured to
 * accept more switches (SLA defaults, notification pause, etc.) over time.
 *
 * Mutations are restricted to Company Admin both in the UI (button disabled)
 * and at the backend (PUT /api/service/config returns 403 otherwise).
 */
export default function ServiceSettings({ user, onLogout }) {
  const [config, setConfig] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  const isAdmin = (user?.role_name || '').trim() === 'Company Admin';

  const fetchConfig = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/service/config`, { headers: authHeaders() });
      setConfig(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load Service settings');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const updateField = async (key, value) => {
    if (!isAdmin) {
      toast.error('Only Company Admin can change these settings.');
      return;
    }
    setSaving(true);
    // Optimistic update for snappy UX
    const prev = config;
    setConfig((c) => ({ ...c, [key]: value }));
    try {
      const { data } = await axios.put(`${API}/service/config`, { [key]: value }, { headers: authHeaders() });
      setConfig(data);
      toast.success('Settings saved');
    } catch (e) {
      setConfig(prev);
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ServiceLayout
      user={user}
      onLogout={onLogout}
      title="Service Settings"
      subtitle="Tune Service-module behaviour without code changes"
    >
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {!isAdmin && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-start gap-2.5" data-testid="service-settings-readonly-banner">
            <ShieldAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
            <div className="text-[13px] text-amber-800">
              <p className="font-semibold">Read-only view</p>
              <p>Only the Company Admin can change Service settings. You can still review the current configuration below.</p>
            </div>
          </div>
        )}

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden">
          <header className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center">
              <SettingsIcon className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-[16px] font-semibold text-slate-800 leading-tight">Job Card Numbering</h2>
              <p className="text-[12.5px] text-slate-500">Control how Job Card numbers are assigned for new jobs.</p>
            </div>
          </header>

          {loading || !config ? (
            <div className="flex items-center justify-center py-16 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading settings…
            </div>
          ) : (
            <div className="px-5 py-5 space-y-4" data-testid="service-settings-form">
              {/* Auto-generation toggle */}
              <div className="flex items-start justify-between gap-4 py-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Hash className="w-4 h-4 text-indigo-500" />
                    <label htmlFor="job-no-auto" className="text-[13.5px] font-semibold text-slate-800">
                      Auto-generate Job Card numbers
                    </label>
                  </div>
                  <p className="text-[12.5px] text-slate-500 mt-1 leading-relaxed">
                    When ON, every new Job Card created from the form is automatically assigned the next sequential
                    number (gap-free per financial year). When OFF, you must enter the Job No manually, or upload via
                    Excel — the duplicate-job-no check stays active in both modes.
                  </p>
                </div>

                <ToggleSwitch
                  id="job-no-auto"
                  checked={!!config.job_no_auto_generate}
                  disabled={!isAdmin || saving}
                  onChange={(v) => updateField('job_no_auto_generate', v)}
                  testId="toggle-job-no-auto"
                />
              </div>

              {/* Behaviour banner — explains the current mode */}
              <div
                className={`rounded-xl border px-4 py-3 flex items-start gap-2.5 ${
                  config.job_no_auto_generate
                    ? 'bg-indigo-50 border-indigo-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid="service-settings-mode-banner"
              >
                <Info className={`w-4 h-4 mt-0.5 shrink-0 ${
                  config.job_no_auto_generate ? 'text-indigo-700' : 'text-amber-700'
                }`} />
                <div className={`text-[12.5px] leading-relaxed ${
                  config.job_no_auto_generate ? 'text-indigo-900' : 'text-amber-900'
                }`}>
                  {config.job_no_auto_generate ? (
                    <>
                      <span className="font-semibold">Auto mode</span> — the New Job Card form fills the Job No
                      automatically. Excel uploads continue to use the Job No from the file.
                    </>
                  ) : (
                    <>
                      <span className="font-semibold">Manual mode</span> — the New Job Card form requires you to type a
                      unique Job No. Excel uploads use the Job No from the file. Duplicates are blocked in both paths.
                    </>
                  )}
                </div>
              </div>

              {/* Audit footer */}
              {config.updated_at && (
                <div className="pt-3 border-t border-slate-100 text-[11.5px] text-slate-500">
                  Last updated {new Date(config.updated_at).toLocaleString()}{' '}
                  {config.updated_by_user_name && <>by <span className="font-medium text-slate-700">{config.updated_by_user_name}</span></>}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Field Visibility Configuration — iter-109 */}
        {!loading && config && (
          <FieldVisibilitySection
            isAdmin={isAdmin}
            initialConfig={config}
            onSaved={(updated) => setConfig(updated)}
          />
        )}
      </div>
    </ServiceLayout>
  );
}

function ToggleSwitch({ id, checked, disabled, onChange, testId }) {
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
        checked ? 'bg-indigo-600' : 'bg-slate-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}
